import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { toast } from 'sonner';
import {
  sellPoolItem,
  sellInventoryItems,
  type ApiClient,
  type IframePoolOrderItem,
  type IframeUnopenedOffer,
} from '../../modules/api';
import { useWallet } from '../../modules/wallet';
import { Backdrop } from '../../modules/design-system';
import { useMyPacksData } from './useMyPacksData';
import { RepackzTab } from './tabs/RepackzTab';
import { DropsTab } from './tabs/DropsTab';
import { DeliveriesTab } from './tabs/DeliveriesTab';
// Detail modal disabled — card tap now toggles selection. Keep the import
// path commented out so we can restore the flow without re-discovering it.
// import { OpenedItemModal } from './cards/OpenedItemModal';
import { MyPacksHeader, type MyPacksTabDef, type MyPacksTabId } from './MyPacksHeader';
import { MyPacksStatsRow } from './MyPacksStatsRow';
import { MyPacksToolbar } from './MyPacksToolbar';
import { MyPacksFooter } from './MyPacksFooter';
import { ShipDisclaimerModal } from './ShipDisclaimerModal';
import { DeliveryNewPage, DeliveryShowPage } from '../Delivery';
import { useMyPacksRoute } from './myPacksRoute';

const TABS: MyPacksTabDef[] = [
  { id: 'repackz', label: 'Repackz' },
  // UI label stays "Cards" per Figma; internal tab id keeps the
  // `drops` name (matches backend terminology / existing call sites).
  { id: 'drops', label: 'Cards' },
  { id: 'deliveries', label: 'Deliveries' },
];

export interface MyPacksPageProps {
  client: ApiClient;
  onBack: () => void;
  onOpenPack: (offer: IframeUnopenedOffer) => void;
  /** Cards-tab card-modal "Buy Another Repack" — switches to that
   *  pool_offer's pack page (parent wires this to setGameId / setView). */
  onBuyAnother?: (poolOfferId: number) => void;
  /** Drop the noise-SVG overlay from the page gradient. Propagated to
   *  the Delivery New / Show pages so the whole flow looks consistent. */
  hideBackgroundPattern?: boolean;
  /** Items handed in from an external entry (the reveal "Confirm Shipping"
   *  CTA) to seed the create-delivery flow. Used once for that delivery,
   *  then the Cards-tab selection drives subsequent ones. */
  deliverySeed?: IframePoolOrderItem[];
}

/**
 * My Packs — three tabs over a dark glass page. Figma 706:1683.
 *
 *   Repackz    — pool_offers with unopened packs, one "Open Now" per row.
 *   Cards      — opened pool_items grid (was "Drops" before the redesign),
 *                with select-all/ship/submit placeholders, a 3-stat header
 *                row and per-card buyback CTA.
 *   Deliveries — empty state — shipping lives on the main site.
 *
 * Background gradient + every visible colour flows through Design
 * Constructor knobs (`--co-MyPacks*-*`); see `packages/theme-constructor/src/schema/components.ts`.
 */
export function MyPacksPage({
  client,
  onBack,
  onOpenPack,
  hideBackgroundPattern = false,
  deliverySeed,
}: MyPacksPageProps) {
  const [tab, setTab] = useState<MyPacksTabId>('drops');
  // Detail-modal state disabled while card tap toggles selection.
  // const [activeItem, setActiveItem] = useState<IframePoolOrderItem | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // One-shot delivery seed from the reveal CTA — captured at mount, used for
  // the delivery it opened, then cleared so a later Cards-tab delivery uses
  // the Cards selection.
  const [seedItems, setSeedItems] = useState<IframePoolOrderItem[] | null>(
    deliverySeed && deliverySeed.length > 0 ? deliverySeed : null,
  );
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const { route, push: navigate } = useMyPacksRoute();
  const data = useMyPacksData(client);
  const wallet = useWallet();

  const selectedItems = useMemo(
    () => data.inventory.filter((i) => selected.has(i.id)),
    [data.inventory, selected],
  );

  const handleItemToggle = useCallback((item: IframePoolOrderItem) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  }, []);

  const selectableIds = useMemo(
    () =>
      data.inventory
        .filter((i) => (i.bestActiveOffer?.grossPrice ?? 0) > 0)
        .map((i) => i.id),
    [data.inventory],
  );

  const handleItemSell = useCallback(
    async (item: IframePoolOrderItem) => {
      if (!item.poolOrderId) return;
      await sellPoolItem(client, item.poolOrderId, item.id);
      void data.refetch();
    },
    [client, data],
  );

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (submitting || selected.size === 0) return;

    // Snapshot each card's gross price BEFORE selling — the backend nulls out
    // best_active_offer once a card is sold, so the payout can't be read after
    // the refetch. Keyed by id so we can sum only the cards that actually sell.
    const grossById = new Map(
      data.inventory.map((i) => [i.id, i.bestActiveOffer?.grossPrice ?? 0]),
    );

    setSubmitting(true);
    try {
      // Cross-order bulk sell, scoped by user on the backend. We always pass
      // the explicit selection (never `{ all: true }`) — the user is selling
      // exactly the cards they picked, not their whole inventory.
      const result = await sellInventoryItems(client, {
        poolItemIds: Array.from(selected),
      });

      if (result.sold.length > 0) {
        // Sum only the cards that actually sold so a partial failure doesn't
        // inflate the toast total.
        const payout = result.sold.reduce(
          (sum, item) => sum + (grossById.get(item.id) ?? 0),
          0,
        );
        const soldLabel = result.sold.length === 1 ? 'card' : 'cards';
        toast.success(
          `Sold ${result.sold.length} ${soldLabel} for $${payout.toFixed(2)}`,
        );
      }
      if (result.failed.length > 0) {
        const failedLabel = result.failed.length === 1 ? 'card' : 'cards';
        toast.error(`${result.failed.length} ${failedLabel} couldn't be sold`);
      }
      // sold === 0 && failed === 0: nothing changed — stay silent.

      setSelected(new Set());
      void wallet.refresh();
      void data.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to sell cards');
    } finally {
      setSubmitting(false);
    }
  }, [client, data, selected, submitting, wallet]);

  if (route.kind === 'delivery-new') {
    return (
      <DeliveryNewPage
        client={client}
        selectedItems={seedItems ?? selectedItems}
        hideBackgroundPattern={hideBackgroundPattern}
        onBack={() => {
          setSeedItems(null);
          navigate({ kind: 'index' });
        }}
        onSuccess={() => {
          // Shipping created — back to My Packs and pivot to the
          // Deliveries tab. Its hook remounts on tab change and refetches.
          setSeedItems(null);
          setSelected(new Set());
          setTab('deliveries');
          navigate({ kind: 'index' });
        }}
      />
    );
  }

  if (route.kind === 'delivery-show') {
    return (
      <DeliveryShowPage
        client={client}
        shippingId={route.id}
        hideBackgroundPattern={hideBackgroundPattern}
        onBack={() => navigate({ kind: 'index' })}
      />
    );
  }

  return (
    <div
      data-page="my-packs"
      style={
        hideBackgroundPattern
          ? ({ '--co-MyPacksPageBackground-noiseOpacity': 0 } as CSSProperties)
          : undefined
      }
    >
      <Backdrop bgImageUrl={null} />
      {/* Optional photo layer above the grey gradient backdrop. When the
          `bgImage` knob is unset the CSS var resolves to `none` and this
          div contributes nothing; otherwise the `imageUrl` formatter
          ships a `url("…")` value rendered at cover-size, centred. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: 'var(--co-MyPacksPageBackground-bgImage, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        data-co-component="MyPacksPageBackground"
        className="@container relative flex min-h-screen w-full flex-col"
      >
        <MyPacksHeader
          tabs={TABS}
          active={tab}
          onChange={setTab}
          onHome={onBack}
          onAddDelivery={() => navigate({ kind: 'delivery-new' })}
        />

        <div className="mx-auto flex w-full max-w-350 flex-1 flex-col">
        {tab === 'drops' && (
          <div
            data-co-component="MyPacksStatsBar"
            className="
              mx-4 my-4
              flex flex-col gap-3
              @[600px]:mx-6 @[600px]:rounded-[24px] @[600px]:py-3 @[600px]:px-4
              @[600px]:[border:1px_solid_rgba(242,242,242,0.10)]
              @[900px]:h-[52px] @[900px]:flex-row
              @[900px]:items-center @[900px]:justify-between
              @[900px]:gap-3 @[900px]:rounded-[40px] @[900px]:py-0 @[900px]:pl-4 @[900px]:pr-2
            "
          >
            <MyPacksStatsRow
              inventory={data.inventory}
              packsOpened={data.inventoryTotal}
            />
            <MyPacksToolbar
              totalSelectable={selectableIds.length}
              selectedCount={selected.size}
              onSelectAll={() => setSelected(new Set(selectableIds))}
              onClear={() => setSelected(new Set())}
              onShip={() => {
                if (selected.size === 0) return;
                setShipModalOpen(true);
              }}
              onSubmit={handleSubmit}
            />
          </div>
        )}

        <main
          className={
            'flex-1 px-4 @[600px]:px-6 @[600px]:pb-12 ' +
            (tab === 'drops' ? 'pb-32' : 'pb-12')
          }
        >
          {tab === 'repackz' && (
            <RepackzTab
              offers={data.unopened}
              loading={data.unopenedLoading}
              onOpen={onOpenPack}
              onBrowse={onBack}
            />
          )}
          {tab === 'drops' && (
            <DropsTab
              items={data.inventory}
              loading={data.inventoryLoading}
              onBrowse={onBack}
              onItemClick={handleItemToggle}
              onItemSell={handleItemSell}
              selectedIds={selected}
            />
          )}
          {tab === 'deliveries' && (
            <DeliveriesTab
              client={client}
              onOpenDelivery={(id) =>
                navigate({ kind: 'delivery-show', id })
              }
            />
          )}
        </main>

        {/* Drops tab has a mobile-only fixed bottom toolbar (Select All
         *  / Buyback All); reserve room under the footer so the bar
         *  doesn't cover the legal links. Toolbar is `@[600px]:hidden`,
         *  so the spacer is gated the same way. */}
        <div className={tab === 'drops' ? 'pb-20 @[600px]:pb-0' : ''}>
          <MyPacksFooter />
        </div>
        </div>
      </div>

      {shipModalOpen && (
        <ShipDisclaimerModal
          client={client}
          poolItemIds={[...selected]}
          onClose={() => setShipModalOpen(false)}
          onAcceptBuyback={() => {
            setShipModalOpen(false);
            void handleSubmit();
          }}
          onAddDelivery={() => {
            setShipModalOpen(false);
            navigate({ kind: 'delivery-new' });
          }}
        />
      )}

      {/* Detail modal disabled — card tap now toggles selection. Restore
          when we want the per-card sell/buy-another flow back.
      {activeItem && (
        <OpenedItemModal
          client={client}
          item={activeItem}
          onClose={() => setActiveItem(null)}
          onSold={() => {
            void data.refetch();
          }}
          onBuyAnother={(offerId) => {
            setActiveItem(null);
            if (onBuyAnother) onBuyAnother(offerId);
            else onBack();
          }}
        />
      )}
      */}
    </div>
  );
}
